/**
 * Dialog – Animate UI style (Radix UI + Motion).
 * Use for all overlays/modals. Supports controlled open/onOpenChange.
 */
import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion } from 'motion/react';
import { X } from 'lucide-react';
import { button, modal, cn } from './uiTheme';
import { isDropdownPortalTarget } from './dropdownPortal';

const DialogRoot = DialogPrimitive.Root;
const DialogTrigger = DialogPrimitive.Trigger;
const DialogClose = DialogPrimitive.Close;
const DialogPortal = DialogPrimitive.Portal;
const DialogTitle = DialogPrimitive.Title;
const DialogDescription = DialogPrimitive.Description;

const overlayTransition = { duration: 0.2, ease: 'easeInOut' as const };
const contentTransition = { type: 'spring' as const, stiffness: 150, damping: 25 };

interface DialogOverlayProps {
  className?: string;
  open?: boolean;
  [key: string]: any;
}

/** Radix `DialogPortal` uses `Portal asChild`, so each portal child must forward refs. */
const DialogOverlay = React.forwardRef<HTMLDivElement, DialogOverlayProps>(function DialogOverlay(
  { className = '', open, ...props },
  forwardedRef
) {
  return (
    <DialogPrimitive.Overlay
      ref={forwardedRef}
      forceMount
      className="fixed inset-0 z-50"
      aria-hidden
      {...props}
    >
      <motion.div
        className={cn(modal.overlay, className)}
        initial={false}
        animate={{ opacity: open ? 1 : 0 }}
        transition={overlayTransition}
        style={{ pointerEvents: open ? 'auto' : 'none' }}
      />
    </DialogPrimitive.Overlay>
  );
});
DialogOverlay.displayName = 'DialogOverlay';

interface DialogContentProps {
  className?: string;
  children?: React.ReactNode;
  showCloseButton?: boolean;
  open?: boolean;
  onClose?: () => void;
  onOpenAutoFocus?: (event: Event) => void;
  onEscapeKeyDown?: (event: KeyboardEvent) => void;
  onPointerDownOutside?: (event: CustomEvent) => void;
  onInteractOutside?: (event: CustomEvent) => void;
  contentClassName?: string;
  style?: React.CSSProperties;
  [key: string]: any;
}

const DialogContent = React.forwardRef<HTMLDivElement, DialogContentProps>(function DialogContent(
  {
    className = '',
    children,
    showCloseButton = true,
    open,
    onClose: _onClose,
    onOpenAutoFocus,
    onEscapeKeyDown,
    onPointerDownOutside,
    onInteractOutside,
    contentClassName,
    style,
    ...props
  },
  forwardedRef
) {
  const hasCustomMaxWidth = contentClassName?.includes('max-w');
  const hasCustomOverflow = contentClassName?.match(/overflow-/);
  const combinedClass = cn(
    modal.container,
    'flex max-h-[90vh] flex-col',
    !hasCustomOverflow && 'overflow-y-auto',
    !hasCustomMaxWidth && 'max-w-lg',
    className,
    contentClassName
  );

  const handlePointerDownOutside = (event: CustomEvent) => {
    if (isDropdownPortalTarget(event.target)) {
      event.preventDefault();
    }
    onPointerDownOutside?.(event);
  };

  const handleInteractOutside = (event: CustomEvent) => {
    if (isDropdownPortalTarget(event.target)) {
      event.preventDefault();
    }
    onInteractOutside?.(event);
  };

  return (
    <DialogPrimitive.Content
      ref={forwardedRef}
      forceMount
      className={`fixed left-[50%] top-[50%] z-50 w-full translate-x-[-50%] translate-y-[-50%] ${combinedClass}`}
      {...props}
      onPointerDownOutside={handlePointerDownOutside}
      onInteractOutside={handleInteractOutside}
      onEscapeKeyDown={onEscapeKeyDown}
      onOpenAutoFocus={onOpenAutoFocus}
      style={{
        ...(style && typeof style === 'object' ? style : {}),
        pointerEvents: open ? 'auto' : 'none',
      }}
    >
      <motion.div
        className="relative flex min-h-0 w-full flex-1 flex-col overflow-hidden"
        initial={false}
        animate={
          open ? { opacity: 1, scale: 1, y: 0, x: 0 } : { opacity: 0, scale: 0.96, y: '-2%', x: 0 }
        }
        transition={contentTransition}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close asChild>
            <button
              type="button"
              className={cn(
                button.icon,
                'absolute right-4 top-4 focus:outline-none focus:ring-2 focus:ring-brand-purple/50 disabled:opacity-50'
              )}
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </DialogPrimitive.Close>
        )}
      </motion.div>
    </DialogPrimitive.Content>
  );
});
DialogContent.displayName = 'DialogContent';

function DialogHeader({ className = '', ...props }: any) {
  return <div className={cn(modal.header, className)} {...props} />;
}

function DialogFooter({ className = '', ...props }: any) {
  return <div className={cn(modal.footer, className)} {...props} />;
}

/**
 * Renders a controlled dialog with animated overlay and content.
 * Pass open to DialogContent when using: <Dialog open={open} onOpenChange={...}><DialogContent open={open}>...</DialogContent></Dialog>
 */
function Dialog({ open, onOpenChange, children }: any) {
  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      {open && (
        <DialogPortal forceMount>
          <DialogOverlay open={open} />
          {children}
        </DialogPortal>
      )}
    </DialogRoot>
  );
}

export {
  Dialog,
  DialogRoot,
  DialogTrigger,
  DialogClose,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
